import React, { useEffect } from 'react';
import { array, bool, func, object, string } from 'prop-types';
import MapSelectorModal from '../MapSelectorModal';
import { StringConstantsMixin } from '../../application/javascripts/react_components/mixins/shared/string_constants_mixin';
import { styleConfigs } from '../../utils/mapbox/style_configs';
import { overlayConfigs } from '../../utils/mapbox/overlay_configs';
import { configCards } from '../../utils/map_selection_helpers';
import { camelToDash } from '../../utils/camel';
import useToggle from '../../hooks/useToggle';

const getMapTypesCardImage = layerKey => {
  return `https://cdn-assets.alltrails.com/assets/map-layers/v2/map-layer-${layerKey}@2x.png`;
};

const getMapDetailsCardImage = layerKey => {
  return `https://cdn-assets.alltrails.com/assets/map-layers/v2/map-overlay-${camelToDash(layerKey)}@2x.png`;
};

const hotkeysHash = {
  87: 'worldparks', // w
  69: 'openstreet', // e
  84: 'topo', // t
  83: 'alltrailsSatellite', // s
  65: 'arcgisSatellite', // a
  66: 'adminBasemap', // b
  89: 'adminOverlay', // y
  82: 'roadmap', // r
  68: 'worldtopo', // d
  81: 'opencycle', // q
  70: 'alltrailsOutdoorsV2', // f
  80: 'heatmap', // p
  79: 'nearbyTrails', // o
  76: 'recordingsAll', // l
  75: 'recordingsPop' // k
};

const MapSelection = ({
  onCardClick,
  onHeatmapClick,
  currentMapLayer,
  permissions,
  onActivityFilterChange,
  enabledOverlays,
  terrainActive,
  handleResetClick,
  selectedFilterActivity,
  adminCustomizationSettings,
  handleAdminCustomizationSettingsChange,
  taller,
  mobileBrowser,
  adminLayout
}) => {
  const [open, toggle] = useToggle(false);

  const body = document.querySelector('body');
  const hideForFullscreen = document.querySelector('.hide-for-fullscreen-modal');

  const closeModal = () => {
    toggle();
    if (body && hideForFullscreen) {
      body.style.overflow = 'unset';
      hideForFullscreen.style.display = '';
    }
  };

  const openModal = () => {
    toggle();
    if (body && hideForFullscreen) {
      body.style.overflow = 'hidden';
      hideForFullscreen.style.display = 'none';
    }
  };

  const isMapTypeCardSelected = layer => {
    return currentMapLayer === layer;
  };

  const isMapDetailsCardSelected = overlay => {
    return enabledOverlays.includes(overlay);
  };

  const mapTypesKeys = adminLayout
    ? ['alltrailsOutdoorsV2', 'openstreet', 'alltrailsSatellite', 'arcgisSatellite', 'worldparks', 'roadmap', 'opencycle', 'topo', 'worldtopo', 'adminBasemap']
    : ['alltrailsOutdoorsV2', 'roadmap', 'alltrailsSatellite', 'topo', 'worldtopo', 'worldparks', 'openstreet', 'opencycle', 'arcgisSatellite'];

  const mapDetailsKeys = adminLayout
    ? [
        'nearbyTrails',
        'nearbyTrailsHeatmap',
        'heatmap',
        'recordingsAll',
        'recordingsAllHeatmap',
        'recordingsPop',
        'recordingsPopHeatmap',
        'after',
        'limit',
        'pendingTrails',
        'pendingTrailsHeatmap',
        'waypoints',
        'photos',
        'mapsAll',
        'clusterTrails',
        'clusterTrailsHeatmap',
        'contribs',
        'distanceMarkers',
        'adminOverlay'
      ]
    : [
        'waypoints',
        'photos',
        'nearbyTrails',
        'distanceMarkers',
        'heatmap',
        'weather',
        'airQuality',
        'lightPollution',
        'pollen',
        'waymarkedCycling',
        'waymarkedHiking',
        'waymarkedMTB'
      ];

  const mapTypesCards = configCards(mapTypesKeys, styleConfigs, permissions, getMapTypesCardImage, isMapTypeCardSelected, null, 'basemap');

  const mapDetailsCards = configCards(
    mapDetailsKeys,
    overlayConfigs,
    permissions,
    getMapDetailsCardImage,
    isMapDetailsCardSelected,
    terrainActive,
    'overlay'
  );

  let additionalOptionsCards = null;

  if (adminLayout) {
    const additionalOptionsKeys = ['weather', 'airQuality', 'lightPollution', 'pollen', 'waymarkedCycling', 'waymarkedHiking', 'waymarkedMTB'];
    additionalOptionsCards = configCards(
      additionalOptionsKeys,
      overlayConfigs,
      permissions,
      getMapDetailsCardImage,
      isMapDetailsCardSelected,
      null,
      'overlay'
    );
  }

  const renderSelectorModal = () => {
    if (!open) {
      return null;
    }

    return (
      <MapSelectorModal
        mapTypesCards={mapTypesCards}
        mapDetailsCards={mapDetailsCards}
        additionalOptionsCards={additionalOptionsCards}
        handleClose={closeModal}
        handleResetClick={handleResetClick}
        handleCardClick={onCardClick}
        handleHeatmapClick={onHeatmapClick}
        handleActivityFilterChange={onActivityFilterChange}
        selectedFilterActivity={selectedFilterActivity}
        adminCustomizationSettings={adminCustomizationSettings}
        handleAdminCustomizationSettingsChange={handleAdminCustomizationSettingsChange}
        taller={taller}
        adminLayout={adminLayout}
        admin
        mobileBrowser={mobileBrowser}
        enabledOverlays={enabledOverlays}
      />
    );
  };

  const activeClass = open ? 'active' : '';

  const handleKeyDown = e => {
    // holding alt and other keys will enable different map layers
    if (e.altKey) {
      const layerKey = hotkeysHash[e.keyCode];
      if (layerKey) {
        const card = [...mapDetailsCards, ...mapTypesCards].find(({ key }) => key === layerKey);
        if (card) {
          onCardClick(card);
        }
      }
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <div id="map-layer-control" className="at-noprint">
      <div
        data-testid="map-options"
        className={`map-control bottom-layers-green ${activeClass}`}
        title={StringConstantsMixin.strings.MAP_OPTIONS}
        onClick={openModal}
      />
      {renderSelectorModal()}
    </div>
  );
};

MapSelection.propTypes = {
  onCardClick: func.isRequired,
  onHeatmapClick: func.isRequired,
  currentMapLayer: string.isRequired,
  permissions: array.isRequired,
  onActivityFilterChange: func.isRequired,
  enabledOverlays: array.isRequired,
  adminCustomizationSettings: object,
  handleAdminCustomizationSettingsChange: func,
  terrainActive: bool,
  taller: bool,
  adminLayout: bool,
  mobileBrowser: bool,
  selectedFilterActivity: string
};

MapSelection.defaultProps = {
  selectedFilterActivity: null,
  adminCustomizationSettings: {},
  taller: false,
  adminLayout: false,
  mobileBrowser: false,
  terrainActive: false
};

export default MapSelection;
